/**
 * The role ladder, mirroring `backend/src/ira/api/auth.py`.
 *
 * This is presentation only. It decides which controls are rendered and which are greyed;
 * it decides nothing about what is permitted. Every route re-derives the principal from the
 * verified token and calls `Principal.require`, so hiding a button here does not protect
 * anything and showing one does not grant anything. `api/auth.py`'s module docstring names a
 * sibling project that authenticates but never authorises — the point of keeping this file
 * thin is that it stays obvious which side is the control.
 *
 * The numbers match the Python `Role` IntEnum so the two can be compared when reading logs.
 */
export const Role = {
  NONE: 0,
  VIEWER: 10,
  ANALYST: 20,
  RESPONDER: 30,
  ADMIN: 40,
} as const;

export type RoleValue = (typeof Role)[keyof typeof Role];

const LABELS: Record<RoleValue, string> = {
  [Role.NONE]: 'no access',
  [Role.VIEWER]: 'viewer',
  [Role.ANALYST]: 'analyst',
  [Role.RESPONDER]: 'responder',
  [Role.ADMIN]: 'admin',
};

export function roleLabel(role: RoleValue): string {
  return LABELS[role];
}

/**
 * App-role names as registered on the API app registration.
 *
 * Compared casefolded, for the reason `RoleMapping.resolve` gives: Entra preserves whatever
 * casing the administrator typed, and `IRA.admin` versus `IRA.Admin` would otherwise deny
 * access with no useful error anywhere.
 */
const ROLE_NAMES: [readonly string[], RoleValue][] = [
  [['ira.admin'], Role.ADMIN],
  [['ira.responder'], Role.RESPONDER],
  [['ira.analyst'], Role.ANALYST],
  [['ira.viewer'], Role.VIEWER],
];

/** Highest matching rank, or NONE. */
export function resolveRole(claimRoles: readonly string[] | undefined): RoleValue {
  const present = new Set((claimRoles ?? []).map((r) => r.trim().toLowerCase()).filter(Boolean));
  for (const [names, role] of ROLE_NAMES) {
    if (names.some((name) => present.has(name))) return role;
  }
  return Role.NONE;
}

/** Claims this app reads off the token. Everything else is ignored. */
export interface IraClaims {
  oid?: string;
  sub?: string;
  tid?: string;
  name?: string;
  preferred_username?: string;
  upn?: string;
  roles?: string[];
}

export interface Principal {
  /** The `oid` claim — the identity the audit trail records. */
  subject: string;
  upn: string;
  name: string;
  role: RoleValue;
  tenantId: string;
  rawRoles: readonly string[];
}

export function principalFromClaims(claims: IraClaims): Principal {
  return {
    subject: (claims.oid ?? claims.sub ?? '').trim(),
    upn: (claims.preferred_username ?? claims.upn ?? '').trim(),
    name: (claims.name ?? '').trim(),
    role: resolveRole(claims.roles),
    tenantId: (claims.tid ?? '').trim(),
    rawRoles: claims.roles ?? [],
  };
}

export function initials(principal: Principal): string {
  const source = principal.name || principal.upn;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '').charAt(0)}${(parts[1] ?? '').charAt(0)}`.toUpperCase();
}
